import React from "react";
import ReactSelect from "react-select";

const Select = ({ 
    multiselect = false, 
    value, 
    onChange, 
    children, 
    className = "", 
    name,
    required = false,
    ...props 
}) => {
    // Create hidden inputs for form submission compatibility
    const hiddenInputs = React.useMemo(() => {
        if (!name || value === null || value === undefined) return null;
        
        if (multiselect && Array.isArray(value)) {
            // For multiselect, create multiple hidden inputs with the same name
            return value.map((val, index) => (
                <input
                    key={`${name}_${index}`}
                    type="hidden"
                    name={name}
                    value={val}
                />
            ));
        } else if (!multiselect && (value !== null && value !== undefined)) {
            // For single select, create one hidden input (including value 0)
            return (
                <input
                    type="hidden"
                    name={name}
                    value={value}
                />
            );
        }
        
        return null;
    }, [name, value, multiselect]);
    // Convert children (option elements) to react-select format
    const options = React.Children.toArray(children)
        .filter(child => child.type === 'option' && child.props.value !== "" && child.props.value !== undefined)
        .map(child => ({
            value: String(child.props.value), // Convert to string to ensure consistency
            label: child.props.children
        }));

    // Convert current value to react-select format
    const getSelectValue = () => {
        if (value === null || value === undefined) return multiselect ? [] : null;
        
        if (multiselect) {
            if (Array.isArray(value)) {
                return value.map(val => options.find(opt => opt.value === String(val))).filter(Boolean);
            }
            return [];
        } else {
            return options.find(opt => opt.value === String(value)) || null;
        }
    };

    // Handle value changes
    const handleChange = (selectedOptions) => {
        if (!onChange) return;

        let newValue;
        if (multiselect) {
            newValue = selectedOptions ? selectedOptions.map(option => option.value) : [];
        } else {
            newValue = selectedOptions ? selectedOptions.value : "";
        }

        // Create synthetic event to match standard input behavior
        const syntheticEvent = {
            target: {
                name,
                value: newValue
            }
        };

        onChange(syntheticEvent);
    };

    // Custom styles to match the existing design system
    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: '#FCF6F1', // beige-light
            border: 'none',
            borderRadius: '0px',
            minHeight: '50px',
            fontSize: '16px',
            fontFamily: 'Degular, sans-serif',
            fontWeight: '500',
            color: '#252525', // black
            boxShadow: 'none',
            '&:hover': {
                border: 'none',
            },
            cursor: 'pointer',
            paddingLeft: '10px',
            minWidth: '150px',
            // paddingRight: '20px'
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0',
        }),
        input: (provided) => ({
            ...provided,
            margin: '0',
            padding: '0',
            color: '#252525',
            fontFamily: 'Degular, sans-serif',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#adadad', // grey-400
            fontSize: '16px',
            fontFamily: 'Degular, sans-serif',
            fontWeight: '500',
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#252525', // black
            fontSize: '16px',
            fontFamily: 'Degular, sans-serif',
            fontWeight: '500',
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: '#252BBE', // blue
            borderRadius: '4px',
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: '#FFFFFF', // white
            fontSize: '14px',
            fontFamily: 'Degular, sans-serif',
            fontWeight: '500',
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: '#FFFFFF',
            '&:hover': {
                backgroundColor: '#5056E5', // blue-light
                color: '#FFFFFF',
            },
        }),
        menu: (provided) => ({
            ...provided,
            borderRadius: '0px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid #ededed',
        }),
        menuList: (provided) => ({
            ...provided,
            padding: '0',
            maxHeight: '200px',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? '#5056E5' : '#FFFFFF', // blue-light on focus
            color: state.isFocused ? '#FFFFFF' : '#252525',
            cursor: 'pointer',
            fontSize: '16px',
            fontFamily: 'Degular, sans-serif',
            fontWeight: '500',
            padding: '12px 20px',
            '&:active': {
                backgroundColor: '#252BBE', // blue
                color: '#FFFFFF',
            },
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            color: '#252525',
            '&:hover': {
                color: '#252BBE',
            },
        }),
        clearIndicator: (provided) => ({
            ...provided,
            color: '#252525',
            '&:hover': {
                color: '#252BBE',
            },
        }),
    };

    return (
        <div className="relative">
            <ReactSelect
                {...props}
                isMulti={multiselect}
                options={options}
                value={getSelectValue()}
                onChange={handleChange}
                styles={customStyles}
                className={`react-select-container ${className}`}
                classNamePrefix="react-select"
                placeholder={multiselect ? "Multi-Select field" : "Select field"}
                noOptionsMessage={() => "Aucune option disponible"}
                isClearable={!required}
                isSearchable={true}
                closeMenuOnSelect={!multiselect}
            />
            {hiddenInputs}
        </div>
    );
};

export default Select;